<template>
  <div
    data-reviews-filters-scroll-target
    class="campsite-reviews-filters-wrapper"
  >
    <pu-slideout-panel
      :trigger-button-class="['reviews-filter-button']"
      position="right"
      class="campsite-reviews-filters"
      @slideout-panel-opened="filterPanelOpened"
      @slideout-panel-closed="filterPanelClosed"
      @slideout-panel-closed-by-button="$emit('closed')"
    >
      <template #trigger-button-contents>
        <span
          v-scrollto="{
            target: '[data-reviews-filters-scroll-target]',
            options: { duration: 350, offset: scrollOffset },
          }"
          class="trigger-button-contents"
        >
          <span class="button-icon start">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill-rule="evenodd"
              stroke-linejoin="round"
              stroke-miterlimit="1.4"
              clip-rule="evenodd"
              viewBox="0 0 45 30"
              class="filter-icon"
            >
              <path
                d="M19 29.3h6.4a2.4 2.4 0 0 0 0-4.9H19c-1.3 0-2.4 1.1-2.4 2.5 0 1.3 1 2.4 2.4 2.4zM12.4 17.6H32c1.4 0 2.5-1.1 2.5-2.4 0-1.4-1.1-2.5-2.5-2.5H12.4c-1.3 0-2.4 1.1-2.4 2.5 0 1.3 1 2.4 2.4 2.4zM2.4 4.9H42c1.4 0 2.5-1.1 2.5-2.5 0-1.3-1.1-2.4-2.5-2.4H2.4C1.1 0 0 1 0 2.4S1 5 2.4 5z"
              />
            </svg>
          </span>
          <translate class="button-text">Filter and sort...</translate>
        </span>
      </template>

      <section class="campsite-reviews-filters-content">
        <header class="filter-reviews">
          <translate tag="h3"> Filter reviews </translate>
        </header>

        <fieldset v-for="filter in filters" :key="filter.label">
          <base-checkbox
            v-if="filter.params.name === 'has_photos'"
            :slide-toggle="true"
            :model-value="selectedFilters.has_photos === true"
            @update:model-value="
              filterChange({ select: 'has_photos', selectedValue: $event })
            "
          >
            <translate>Only show reviews with photos</translate>
          </base-checkbox>
          <base-select
            v-else
            v-bind="filter.params"
            css-class="asblock"
            :data-sctrack="`${String($route.name)}--CampsiteReviewsFilters-select`"
            @change="filter.changeHandler"
          >
            <template #label>
              {{ filter.label }}
            </template>
            <template #label-status> &#8203; </template>
          </base-select>
        </fieldset>
      </section>

      <template #additional-close-button>
        <translate
          tag="p"
          :tt="{ numberOfMonthsToShowReviewsFor: '30' }"
          class="showing-reviews-note"
          >Only showing reviews for the last {numberOfMonthsToShowReviewsFor}
          months.
        </translate>
        <section class="apply-button">
          <base-button css-class="secondary asblock" @click="applyFilters">
            <translate>Apply</translate>
          </base-button>
        </section>
      </template>
    </pu-slideout-panel>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import CampsiteReviewFilterOptionsGrouper from '../filter-options-grouper'
import type { FilterCounts } from '../types'
import type { Filters, SortAndFilters } from './types'

@Component({
  emits: ['filter-select-changed', 'closed'],
})
class CampsiteReviewsFilters extends Vue {
  @Prop({ required: true })
  selectedSort: string

  @Prop({ required: true })
  selectedFilters: Filters

  @Prop({ required: true })
  filterCounts: FilterCounts

  @Prop({ required: true })
  private filterOptions

  private selected: SortAndFilters = {}
  private changedFilters: SortAndFilters = {}

  scrollOffset = this.$isDesktop ? -75 : -23

  // workaround for webkit zindex and stacking order bugs with absolute and fixed position elements
  filterPanelOpened() {
    this.changedFilters = {}
    if (document && document.body) {
      document.body.setAttribute('data-reviews-filters-open', 'true')
    }
  }

  filterPanelClosed() {
    if (document && document.body) {
      document.body.removeAttribute('data-reviews-filters-open')
    }
  }

  beforeUnmount() {
    if (this && this.filterPanelClosed) {
      this.filterPanelClosed()
    }
  }

  private optionsByType(type) {
    return Object.values(this.groupedByType[type] || {}).map((option) => {
      let name = option.option.value
      if (option.count > 0) {
        name = `${name} (${option.count})`
      }
      return {
        name,
        value: option.option.name,
        selected: this.selectedFilters[type] === option.option.name,
        disabled: option.count === 0,
      }
    })
  }

  filterChange({ select, selectedValue }) {
    this.selected[select] = selectedValue
    this.changedFilters[select] = selectedValue
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { sorting, ...changedFilters } = this.changedFilters
    this.$emit('filter-select-changed', {
      ...this.selectedFilters,
      ...changedFilters,
    })
  }

  sortChange({ selectedValue }) {
    this.changedFilters.sorting = selectedValue
  }

  applyFilters() {
    if (Object.keys(this.changedFilters).length) {
      this.$emit('filters-and-sort-changed', this.changedFilters)
    }
  }

  get sortOptions() {
    return [
      {
        name: this.$t('Arrival date (latest first)'),
        value: '-visit_date',
      },
      {
        name: this.$t('Overall rating (lowest first)'),
        value: 'rate_overall',
      },
      {
        name: this.$t('Overall rating (highest first)'),
        value: '-rate_overall',
      },
    ].map((option) => ({
      ...option,
      selected: this.selectedSort === option.value,
    }))
  }

  get filters() {
    return [
      {
        label: this.$t('Sort by'),
        changeHandler: this.sortChange,
        params: {
          name: 'sort',
          selectOptions: this.sortOptions,
        },
      },
      {
        label: this.$t('Only show reviews with photos'),
        changeHandler: this.filterChange,
        params: {
          name: 'has_photos',
          selectOptions: this.hasPhotosOptions,
          defaultValue: 'false',
        },
      },
      {
        label: this.$t('Show me reviews by'),
        changeHandler: this.filterChange,
        params: {
          name: 'groupType',
          selectOptions: this.optionsByType('groupType'),
          defaultSelectName: this.$t('All types of reviewers'),
        },
      },
      {
        label: this.$t('Staying in'),
        changeHandler: this.filterChange,
        params: {
          name: 'unitType',
          selectOptions: this.optionsByType('unitType'),
          defaultSelectName: this.$t('All types of accommodation'),
        },
      },
      {
        label: this.$t('Original language of review'),
        changeHandler: this.filterChange,
        params: {
          name: 'language',
          selectOptions: this.optionsByType('language'),
          defaultSelectName: this.$t('All languages'),
        },
      },
    ]
  }

  get groupedByType() {
    const grouper = new CampsiteReviewFilterOptionsGrouper(this.filterOptions)
    return grouper.getCounted(this.filterCounts)
  }

  get hasPhotosOptions() {
    return [
      { name: this.$t('All reviews'), value: 'false' },
      { name: this.$t('With photos'), value: 'true' },
    ].map((option) => ({
      ...option,
      selected:
        String(this.selectedFilters.has_photos) === option.value ||
        (option.value === 'false' && !this.selectedFilters.has_photos),
    }))
  }
}

export default toNative(CampsiteReviewsFilters)
</script>

<style>
[data-reviews-filters-open] .page-content {
  visibility: hidden;
  overflow: hidden;
  min-height: 0;
}

[data-reviews-filters-open] .back-to-top {
  visibility: hidden !important;
}

[data-reviews-filters-open] .page-content::-webkit-scrollbar {
  display: none;
}

[data-reviews-filters-open] .ratings-distribution .rating {
  display: none;
}

.reviews-filter-button {
  border-radius: var(--border-radius);
  border: 2px solid var(--colour-page-background);
  box-shadow: none;
  display: flex;
  justify-content: center;
  padding: 0.75em 1em;
  width: 100%;
}

.reviews-filter-button .button-text {
  color: var(--colour-dark-blue);
  flex-grow: 1;
  font-weight: 500;
  text-align: left;
}

.reviews-filter-button .button-icon {
  flex-shrink: 0;
  align-self: center;
  display: block;
  height: auto;
}

.reviews-filter-button .button-icon.start {
  margin: 1px 0.5em 0 0;
}

.reviews-filter-button:active,
.reviews-filter-button:focus-visible {
  box-shadow: none;
  background: #ced6da;
  border-color: #ced6da;
  outline: none;
  transform: none;
}
</style>

<style scoped>
label {
  display: block;
}

section {
  padding: 0 1.4em 0.5em;
}

.trigger-button-contents {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-icon {
  fill: var(--colour-dark-blue);
  height: 0.8em;
  width: auto;
}

.filter-reviews {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  min-height: 4em;
  border-bottom: 1px solid var(--colour-page-background);
}

.campsite-reviews-filters-content header.filter-reviews {
  padding: 0;
}

header h3 {
  margin: 0;
}

.campsite-reviews-filters-content :deep(fieldset:first-of-type) {
  background: #f0f1f3;
  padding: 0.25em 0.5em 0.5em;
  border-radius: var(--border-radius);
}

.campsite-reviews-filters-content :deep(.base-checkbox) {
  border-top: 1px solid var(--colour-page-background-light);
  border-bottom: 1px solid var(--colour-page-background-light);
  padding: 0.5em 0;
}

.campsite-reviews-filters-content :deep(.base-checkbox label) {
  justify-content: space-between;
  font-weight: normal;
  width: 100%;
}

.showing-reviews-note {
  font-size: var(--small);
  text-align: center;
  margin: 0 0 0.75em 0;
  color: var(--colour-text-light);
}
</style>
